import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { getPublicMediaUrl } from '../lib/mediaHelpers';

interface SongArtworkUploadProps {
  currentImageUrl: string | null;
  songId: string | null;
  onUploadComplete: (url: string) => void;
  onRemove: () => void;
}

export default function SongArtworkUpload({
  currentImageUrl,
  songId,
  onUploadComplete,
  onRemove
}: SongArtworkUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      // Generate unique filename
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const ext = file.name.split('.').pop();
      const filename = `${timestamp}-${randomStr}.${ext}`;

      // Upload to Supabase Storage (album-art bucket)
      const { data, error } = await supabase.storage
        .from('album-art')
        .upload(filename, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      // Return just the filename - mediaHelpers will generate the full URL
      onUploadComplete(filename);
    } catch (err: any) {
      console.error('Upload error:', err);
      setUploadError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  const displayImageUrl = currentImageUrl ? getPublicMediaUrl(currentImageUrl) : null;

  return (
    <div className="w-[300px] flex flex-col items-center space-y-3">
      {/* Artwork Preview - Fixed Size */}
      {displayImageUrl ? (
        <div className="relative w-[180px] h-[180px] mt-2">
          <img
            src={displayImageUrl}
            alt="Song artwork"
            className="w-full h-full rounded-lg object-cover border border-[#282828]"
          />
          <button
            type="button"
            onClick={onRemove}
            className="absolute -top-2 -right-2 p-1.5 bg-[#FF6B6B] rounded-full text-white shadow-lg hover:bg-[#FF5252] transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="w-[180px] h-[180px] mt-2 border-2 border-dashed border-[#383838] rounded-lg flex flex-col items-center justify-center bg-[#0A0A0A]">
          <ImageIcon className="w-10 h-10 text-[#606060] mb-1.5" />
          <p className="text-[11px] text-[#707070]">No artwork uploaded</p>
        </div>
      )}

      {/* Upload Button - Matches Preview Width */}
      <div className="w-[180px]">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id="artwork-upload"
        />
        <label
          htmlFor="artwork-upload"
          className={`flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${
            uploading
              ? 'bg-[#282828] text-[#808080] cursor-not-allowed'
              : 'bg-[#40E0D0] text-[#0A0A0A] hover:bg-[#47EAD8]'
          }`}
        >
          <Upload className="w-3.5 h-3.5" />
          {uploading ? 'Uploading...' : displayImageUrl ? 'Replace artwork' : 'Upload artwork'}
        </label>
      </div>

      {/* Error Message - Full Column Width */}
      {uploadError && (
        <div className="w-full px-2.5 py-1.5 bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 rounded">
          <p className="text-[11px] text-[#FF6B6B]">{uploadError}</p>
        </div>
      )}

      {/* Helper Text - Full Column Width, Single Line */}
      <p className="w-full text-[10px] text-[#505050] leading-relaxed text-center">
        Square image · 600×600 min · 5MB max
      </p>
    </div>
  );
}
